import type {
  WsAccount,
  WsCommandAlias,
  WsEvent,
  WsFeed,
  WsFeedGroup,
  WsFeedGroupMembership,
  WsFeedPreference,
  WsItem,
  WsPermission,
  WsTemplate,
  WorkspaceMembership as WsWorkspaceMembership,
} from "@storyboard/web-client";
import {
  bootstrapWorkspaceAccounts,
  bootstrapWorkspaceCommandAliases,
  bootstrapWorkspaceFeedGroupMemberships,
  bootstrapWorkspaceFeedGroups,
  bootstrapWorkspaceFeedItemsPaginated,
  bootstrapWorkspaceFeedPermissions,
  bootstrapWorkspaceFeedPreferences,
  bootstrapWorkspaceFeeds,
  bootstrapWorkspaceMemberships,
  bootstrapWorkspaceTemplates,
  getUserInfo,
  getWorkspaceInvitations,
  latestUnreadEventPerFeed,
  latestUnreadEventPerFeedForOrganizers,
} from "@storyboard/web-client";
import { transport } from "@/config";
import { getWorkspaceConfig } from "@/data/workspaceConfig";
import { ACKNOWLEDGEMENT_SUBJECTS } from "@/models/acknowledgement/acknowledgementStore";
import { walkAcknowledgementDelta } from "@/models/acknowledgement/acknowledgementSync";
import { warmupSettingsFor } from "@/models/actions/warmupConstants";
import { loadWorkspaceWorkflowItems } from "@/models/actions/workflows";
import { syncWorkspaceMembershipAvailabilities } from "@/models/availabilitySync";
import { syncCurrentRoutes } from "@/models/currentRoutes/currentRoutesSync";
import { aliasFeedIdsOwnedBy } from "@/models/feedAliasMetadata";
import { syncFeedItems } from "@/models/feedItem/feedItemSync";
import { bulkUpsertAccount } from "@/models/repositories/Account";
import { bulkUpsertAccountEvent } from "@/models/repositories/AccountEvent";
import { bulkUpsertDirectWsInvitation } from "@/models/repositories/DirectWsInvitation";
import { bulkUpsertFeed } from "@/models/repositories/Feed";
import { bulkUpsertFeedGroup } from "@/models/repositories/FeedGroup";
import { bulkUpsertFeedGroupMembership } from "@/models/repositories/FeedGroupMembership";
import { bulkUpsertFeedPreference } from "@/models/repositories/FeedPreference";
import { bulkUpsertItemAndCalculateMembership } from "@/models/repositories/Item";
import { bulkUpsertPermission } from "@/models/repositories/Permission";
import { bulkUpsertTemplate } from "@/models/repositories/Template";
import { bulkUpsertWorkspace } from "@/models/repositories/Workspace";
import { bulkUpsertWorkspaceCommandAlias } from "@/models/repositories/WorkspaceCommandAlias";
import { bulkUpsertWorkspaceMembership } from "@/models/repositories/WorkspaceMembership";
import {
  acknowledgementSyncTable,
  type CursorSyncTable,
  type RefetchSyncTable,
  tmsEventSyncTable,
} from "@/models/sync/signals";
import type { SyncDelta } from "@/models/sync/syncDelta";
import { pollOnce } from "@/models/tagPoller";
import { TMS_EVENT_STREAMS } from "@/models/tmsEvent/stream";
import { walkTmsEventDelta } from "@/models/tmsEvent/tmsEventSync";

export type SyncContext = { workspaceId: string; accountId: string };

/** One page of a cursor walk; `pageSize` is the size the server actually served when it clamped the request. */
export type SyncPage = { records: readonly unknown[]; pageSize?: number };

/** A table synced by walking its delta route forward from a persisted cursor. */
export type CursorSyncTarget = {
  kind: "cursor";
  table: CursorSyncTable;
  /** Registry key of the incremental cursor. */
  cursorKey: string;
  /** Registry key of the bootstrap item whose cursor seeds a missing incremental cursor. */
  bootstrapEvent?: string;
  pageSize: number;
  fetchPage: (request: SyncContext & { page: number; pageSize: number; timestamp: string }) => Promise<SyncPage>;
  apply: (records: readonly unknown[], delta: SyncDelta) => Promise<void>;
};

/** A table whose only read path is a whole-set fetch, or that already owns its own cursor. */
export type RefetchSyncTarget = {
  kind: "refetch";
  table: RefetchSyncTable;
  /**
   * The refetch walks its own persisted cursor, so it is idempotent and cheap to re-run: a gap-recovery resync
   * re-walks it beside the cursor tables. A whole-set refetch leaves this unset and waits for its signal.
   */
  ownsCursor?: boolean;
  refetch: (ctx: SyncContext) => Promise<void>;
};

export type SyncTarget = CursorSyncTarget | RefetchSyncTarget;

/** Rows per delta page; the bootstrap routes clamp above 10,000 and default an omitted size to 1,000. */
export const SYNC_PAGE_SIZE = 1_000;

function cursorTarget<T>({
  table,
  bootstrapEvent,
  fetchPage,
  apply,
  pageSize = SYNC_PAGE_SIZE,
}: {
  table: CursorSyncTable;
  bootstrapEvent?: string;
  fetchPage: CursorSyncTarget["fetchPage"];
  apply: (records: T[], delta: SyncDelta) => Promise<void>;
  pageSize?: number;
}): CursorSyncTarget {
  return {
    kind: "cursor",
    table,
    cursorKey: `${table}IncrementalCursor`,
    bootstrapEvent,
    pageSize,
    fetchPage,
    apply: (records, delta) => apply(records as T[], delta),
  };
}

function feedIdsOf(rows: readonly { feedId?: string | null }[]): string[] {
  return rows.map((row) => row.feedId).filter((id): id is string => Boolean(id));
}

/**
 * Whether the workspace shows the Dispatch page, which turns on its TMS events and current routes
 * (`web.dispatch.enabled`).
 */
function dispatchEnabledFor(workspaceId: string): boolean {
  return getWorkspaceConfig(workspaceId)?.web?.dispatch?.enabled === true;
}

export const syncTargets: readonly SyncTarget[] = [
  cursorTarget<WsAccount>({
    table: "accounts",
    bootstrapEvent: "accounts",
    fetchPage: async (request) => {
      const response = await bootstrapWorkspaceAccounts(transport, request);
      return { records: response?.accounts ?? [], pageSize: response?.pageSize };
    },
    apply: (records) => bulkUpsertAccount(records),
  }),
  cursorTarget<WsWorkspaceMembership>({
    table: "workspaceMemberships",
    bootstrapEvent: "workspaceMemberships",
    fetchPage: async (request) => {
      const response = await bootstrapWorkspaceMemberships(transport, request);
      return { records: response?.workspaceMemberships ?? [], pageSize: response?.pageSize };
    },
    apply: async (records, delta) => {
      await bulkUpsertWorkspaceMembership(records);
      // An alias owner's membership can land after the alias and its items; their feeds' items re-derive then.
      for (const feedId of aliasFeedIdsOwnedBy(records.map((membership) => membership.id))) {
        delta.aliasFeedIds.add(feedId);
      }
    },
  }),
  cursorTarget<WsFeed>({
    table: "feeds",
    bootstrapEvent: "feeds",
    fetchPage: async (request) => {
      const response = await bootstrapWorkspaceFeeds(transport, request);
      return { records: response?.feeds ?? [], pageSize: response?.pageSize };
    },
    apply: async (records, delta) => {
      await bulkUpsertFeed(records);
      for (const feed of records) delta.aliasFeedIds.add(feed.id);
    },
  }),
  cursorTarget<WsPermission>({
    table: "permissions",
    bootstrapEvent: "permissions",
    fetchPage: async (request) => {
      const response = await bootstrapWorkspaceFeedPermissions(transport, { ...request, mine: true });
      return { records: response?.permissions ?? [], pageSize: response?.pageSize };
    },
    apply: async (records, delta) => {
      await bulkUpsertPermission(records);
      for (const feedId of feedIdsOf(records)) delta.aliasFeedIds.add(feedId);
    },
  }),
  cursorTarget<WsFeedGroup>({
    table: "feedGroups",
    bootstrapEvent: "feedGroups",
    fetchPage: async (request) => {
      const response = await bootstrapWorkspaceFeedGroups(transport, request);
      return { records: response?.feedGroups ?? [], pageSize: response?.pageSize };
    },
    apply: (records) => bulkUpsertFeedGroup(records),
  }),
  cursorTarget<WsFeedGroupMembership>({
    table: "feedGroupMemberships",
    bootstrapEvent: "feedGroupMemberships",
    fetchPage: async (request) => {
      const response = await bootstrapWorkspaceFeedGroupMemberships(transport, request);
      return { records: response?.feedGroupMemberships ?? [], pageSize: response?.pageSize };
    },
    apply: (records) => bulkUpsertFeedGroupMembership(records),
  }),
  cursorTarget<WsCommandAlias>({
    table: "commandAliases",
    bootstrapEvent: "commandAliases",
    fetchPage: async (request) => {
      const response = await bootstrapWorkspaceCommandAliases(transport, request);
      return { records: response?.commandAliases ?? [], pageSize: response?.pageSize };
    },
    apply: async (records, delta) => {
      await bulkUpsertWorkspaceCommandAlias(records);
      for (const feedId of feedIdsOf(records)) delta.aliasFeedIds.add(feedId);
    },
  }),
  cursorTarget<WsTemplate>({
    table: "templates",
    bootstrapEvent: "templates",
    fetchPage: async (request) => {
      const response = await bootstrapWorkspaceTemplates(transport, request);
      return { records: response?.templates ?? [], pageSize: response?.pageSize };
    },
    apply: (records) => bulkUpsertTemplate(records),
  }),
  cursorTarget<WsFeedPreference>({
    table: "feedPreferences",
    bootstrapEvent: "feedPreferences",
    fetchPage: async (request) => {
      const response = await bootstrapWorkspaceFeedPreferences(transport, { ...request, mine: true });
      return { records: response?.feedPreferences ?? [], pageSize: response?.pageSize };
    },
    apply: (records) => bulkUpsertFeedPreference(records),
  }),
  // Every item created, edited or soft-deleted in any readable feed since the cursor, oldest change first. This is
  // the one call that makes "everything from that item forward" hold for every feed at once.
  cursorTarget<WsItem>({
    table: "items",
    bootstrapEvent: "items",
    fetchPage: async (request) => {
      const response = await bootstrapWorkspaceFeedItemsPaginated(transport, {
        ...request,
        sortAscending: true,
        useCreatedAt: false,
      });
      return { records: response?.items ?? [], pageSize: response?.pageSize };
    },
    apply: async (records, delta) => {
      const upserted = await bulkUpsertItemAndCalculateMembership(records, { fromAppSync: true });
      delta.upsertedItems.push(...upserted);
      for (const item of records) {
        delta.itemIds.add(item.id);
        if (item.feedId) delta.feedIds.add(item.feedId);
      }
    },
  }),
  cursorTarget<WsEvent>({
    table: "unreadEvents",
    bootstrapEvent: "events",
    fetchPage: async (request) => {
      const response = await latestUnreadEventPerFeed(transport, request);
      return { records: response?.events ?? [], pageSize: response?.pageSize };
    },
    apply: async (records, delta) => {
      await bulkUpsertAccountEvent(records);
      for (const feedId of feedIdsOf(records)) delta.feedIds.add(feedId);
    },
  }),
  cursorTarget<WsEvent>({
    table: "organizerUnreadEvents",
    bootstrapEvent: "events",
    fetchPage: async (request) => {
      const response = await latestUnreadEventPerFeedForOrganizers(transport, request);
      return { records: response?.events ?? [], pageSize: response?.pageSize };
    },
    apply: async (records, delta) => {
      await bulkUpsertAccountEvent(records);
      for (const feedId of feedIdsOf(records)) delta.feedIds.add(feedId);
    },
  }),
  {
    kind: "refetch",
    table: "workspaces",
    refetch: async () => {
      const user = await getUserInfo(transport);
      await bulkUpsertWorkspace(user?.workspaces ?? []);
    },
  },
  {
    kind: "refetch",
    table: "directWorkspaceInvitations",
    refetch: async ({ workspaceId }) => {
      const response = await getWorkspaceInvitations(transport, { workspaceId });
      await bulkUpsertDirectWsInvitation(response?.directInvitations ?? []);
    },
  },
  {
    kind: "refetch",
    table: "workflows",
    refetch: ({ workspaceId, accountId }) => loadWorkspaceWorkflowItems({ workspaceId, myAccountId: accountId }),
  },
  {
    kind: "refetch",
    table: "membershipAvailabilities",
    refetch: ({ workspaceId }) => syncWorkspaceMembershipAvailabilities(workspaceId),
  },
  // No producer publishes tag rows today (SVS-2514); when one returns, the signal just brings the poll forward.
  {
    kind: "refetch",
    table: "tags",
    refetch: ({ workspaceId }) => pollOnce(workspaceId),
  },
  // The board's current routes, synced from their stored cursor. No producer pings them, so this runs on a resync; the
  // poller drives them otherwise. Skipped without the Dispatch page.
  {
    kind: "refetch",
    table: "currentRoutes",
    ownsCursor: true,
    refetch: (ctx) => (dispatchEnabledFor(ctx.workspaceId) ? syncCurrentRoutes(ctx) : Promise.resolve()),
  },
  // The item event log the warmup walks, in the classic and the Dispatch view alike. Only the `workspace` mode re-walks
  // on a ping, so every feed's span stays current there; under the batched modes, and where warmup is off, the realtime
  // subscription and the `items` cursor keep the store current and the spans stay where the warmup left them.
  {
    kind: "refetch",
    table: "feedItems",
    ownsCursor: true,
    refetch: (ctx) => {
      const settings = warmupSettingsFor(ctx.workspaceId);
      return settings.mode === "workspace" ? syncFeedItems(ctx) : Promise.resolve();
    },
  },
  // One target per TMS event stream, each walking its own delta from the stream's stored cursor. A
  // workspace without the Dispatch page shows no events, so its walk is skipped and a ping costs no request.
  ...TMS_EVENT_STREAMS.map(
    (stream): RefetchSyncTarget => ({
      kind: "refetch",
      table: tmsEventSyncTable(stream),
      ownsCursor: true,
      refetch: (ctx) => (dispatchEnabledFor(ctx.workspaceId) ? walkTmsEventDelta(ctx, stream) : Promise.resolve()),
    }),
  ),
  // One target per acknowledgement subject, the Dispatch page's handled layer: each walks its own log from its stored
  // cursor, so a colleague's acknowledgement reaches the fold from the server. Skipped without the Dispatch page.
  ...ACKNOWLEDGEMENT_SUBJECTS.map(
    (subject): RefetchSyncTarget => ({
      kind: "refetch",
      table: acknowledgementSyncTable(subject),
      ownsCursor: true,
      refetch: (ctx) =>
        dispatchEnabledFor(ctx.workspaceId) ? walkAcknowledgementDelta(ctx, subject) : Promise.resolve(),
    }),
  ),
];
